import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useAnimation } from '../../hooks/useAnimation';

export default function ReservationPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    floorType: '',
    visitDate: '',
    visitTime: '',
    visitors: '1',
    agreePrivacy: false,
  });

  const infoSection = useAnimation({ threshold: 0.2 });
  const formSection = useAnimation({ threshold: 0.2, delay: 100 });
  const card1 = useAnimation({ threshold: 0.3, delay: 0 });
  const card2 = useAnimation({ threshold: 0.3, delay: 100 });
  const card3 = useAnimation({ threshold: 0.3, delay: 200 });
  const card4 = useAnimation({ threshold: 0.3, delay: 300 });
  const mapSection = useAnimation({ threshold: 0.3, delay: 400 });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.agreePrivacy) return;
    setFormStatus('submitting');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('phone', formData.phone);
      body.append('floorType', formData.floorType);
      body.append('visitDate', formData.visitDate);
      body.append('visitTime', formData.visitTime);
      body.append('visitors', formData.visitors);
      body.append('agreePrivacy', '동의');

      const res = await fetch('https://readdy.ai/api/form/d6ftbi0r225iqdhs0g60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        setShowConfirmModal(true);
        setFormData({ name: '', phone: '', floorType: '', visitDate: '', visitTime: '', visitors: '1', agreePrivacy: false });
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
          onClick={() => { setShowConfirmModal(false); setFormStatus('idle'); }}
        >
          <div
            className="bg-white border-2 border-[#1a2b5c] rounded-2xl p-6 md:p-10 max-w-[340px] md:max-w-[440px] w-full mx-4 text-center shadow-2xl animate-[scaleIn_0.3s_ease-out]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 md:w-16 md:h-16 border-2 border-[#1a2b5c] rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
              <i className="ri-check-line text-[#1a2b5c] text-2xl md:text-3xl"></i>
            </div>
            <h3 className="text-[17px] md:text-[22px] font-bold text-[#1a2b5c] mb-2 md:mb-3" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              방문예약이 완료되었습니다
            </h3>
            <p className="text-[12px] md:text-[15px] text-gray-600 leading-relaxed mb-6 md:mb-8">
              빠른 시일 내에 전문 상담사가<br />연락드리겠습니다. 감사합니다.
            </p>
            <button
              onClick={() => { setShowConfirmModal(false); setFormStatus('idle'); }}
              className="px-8 md:px-10 py-2.5 md:py-3 border-2 border-[#1a2b5c] text-[#1a2b5c] rounded-full text-[13px] md:text-[15px] font-semibold hover:bg-[#1a2b5c] hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="relative pt-16 md:pt-20">
        <div className="relative h-[180px] md:h-[320px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/e69f5483ea6b05e66b55f8d5b9235587.jpg"
            alt="방문예약"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a2b5c]/70 via-[#1a2b5c]/50 to-[#1a2b5c]/70"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center animate-[fadeIn_0.8s_ease-out]">
            <p className="text-white/70 text-[10px] md:text-[13px] tracking-[0.3em] uppercase mb-1.5 md:mb-3">Visit Reservation</p>
            <h1 className="text-[20px] md:text-[42px] font-bold text-white" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              방문예약
            </h1>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 md:py-20 bg-white">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-start">

            {/* Left: Info */}
            <div ref={infoSection.ref} className={`transition-all duration-700 ${infoSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h2 className="text-[18px] md:text-[32px] font-bold text-[#1a2b5c] mb-2 md:mb-4 leading-tight" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                모델하우스 방문예약
              </h2>
              <p className="text-[11px] md:text-[16px] text-gray-600 mb-5 md:mb-10 leading-relaxed">
                가경 센트럴 더 플래티넘의 모델하우스를 방문하시면 전문 상담사가 자세한 안내를 도와드립니다.
              </p>

              <div className="space-y-4 md:space-y-8">
                <div ref={card1.ref} className={`flex gap-2.5 md:gap-4 transition-all duration-600 ${card1.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                  <div className="w-9 h-9 md:w-14 md:h-14 rounded-full bg-[#1a2b5c]/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-lg md:text-2xl text-[#1a2b5c]"></i>
                  </div>
                  <div>
                    <h3 className="text-[12px] md:text-lg font-bold text-gray-900 mb-0.5 md:mb-2">모델하우스 위치</h3>
                    <p className="text-[10px] md:text-base text-gray-600 leading-relaxed">
                      충청북도 청주시 흥덕구 가경동 1018번지<br />
                      가경 더 플래티넘 센트럴 모델하우스
                    </p>
                  </div>
                </div>

                <div ref={card2.ref} className={`flex gap-2.5 md:gap-4 transition-all duration-600 ${card2.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                  <div className="w-9 h-9 md:w-14 md:h-14 rounded-full bg-[#1a2b5c]/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-lg md:text-2xl text-[#1a2b5c]"></i>
                  </div>
                  <div>
                    <h3 className="text-[12px] md:text-lg font-bold text-gray-900 mb-0.5 md:mb-2">운영 시간</h3>
                    <p className="text-[10px] md:text-base text-gray-600 leading-relaxed">
                      평일 · 주말 10:00 – 18:00<br />
                      <span className="text-[9px] md:text-sm text-gray-500">연중무휴 운영</span>
                    </p>
                  </div>
                </div>

                <div ref={card3.ref} className={`flex gap-2.5 md:gap-4 transition-all duration-600 ${card3.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                  <div className="w-9 h-9 md:w-14 md:h-14 rounded-full bg-[#1a2b5c]/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-lg md:text-2xl text-[#1a2b5c]"></i>
                  </div>
                  <div>
                    <h3 className="text-[12px] md:text-lg font-bold text-gray-900 mb-0.5 md:mb-2">전화 상담</h3>
                    <a href="tel:1555-5475"
                      onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
                      className="text-[11px] md:text-base text-gray-600 hover:text-[#1a2b5c] transition-colors font-medium">
                      1555-5475
                    </a>
                  </div>
                </div>

                <div ref={card4.ref} className={`flex gap-2.5 md:gap-4 transition-all duration-600 ${card4.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                  <div className="w-9 h-9 md:w-14 md:h-14 rounded-full bg-[#1a2b5c]/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-car-line text-lg md:text-2xl text-[#1a2b5c]"></i>
                  </div>
                  <div>
                    <h3 className="text-[12px] md:text-lg font-bold text-gray-900 mb-0.5 md:mb-2">주차 안내</h3>
                    <p className="text-[10px] md:text-base text-gray-600 leading-relaxed">
                      모델하우스 전용 주차장 완비<br />
                      <span className="text-[9px] md:text-sm text-gray-500">방문 시 무료 주차 가능</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div ref={mapSection.ref} className={`mt-5 md:mt-10 rounded-xl overflow-hidden shadow-md border border-gray-100 transition-all duration-700 ${mapSection.isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3213.0!2d127.4!3d36.6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z7LKt7LKt!5e0!3m2!1sko!2skr!4v1700000000000!5m2!1sko!2skr"
                  width="100%"
                  height="160"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="모델하우스 위치"
                ></iframe>
              </div>
            </div>

            {/* Right: Form */}
            <div ref={formSection.ref} className={`bg-gray-50 rounded-2xl p-5 md:p-8 shadow-lg transition-all duration-700 ${formSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h3 className="text-[16px] md:text-[22px] font-bold text-[#1a2b5c] mb-4 md:mb-6" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                예약 신청서
              </h3>
              <form
                id="reservation-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4 md:space-y-5"
              >
                {/* 이름 */}
                <div>
                  <label htmlFor="name" className="block text-[12px] md:text-sm font-medium text-gray-900 mb-1.5 md:mb-2">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a2b5c] focus:border-transparent outline-none transition-all text-[12px] md:text-sm"
                    placeholder="홍길동"
                  />
                </div>

                {/* 연락처 */}
                <div>
                  <label htmlFor="phone" className="block text-[12px] md:text-sm font-medium text-gray-900 mb-1.5 md:mb-2">
                    연락처 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a2b5c] focus:border-transparent outline-none transition-all text-[12px] md:text-sm"
                    placeholder="010-0000-0000"
                  />
                </div>

                {/* 관심 평형 */}
                <div>
                  <label htmlFor="floorType" className="block text-[12px] md:text-sm font-medium text-gray-900 mb-1.5 md:mb-2">
                    관심 평형
                  </label>
                  <select
                    id="floorType"
                    name="floorType"
                    value={formData.floorType}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a2b5c] focus:border-transparent outline-none transition-all text-[12px] md:text-sm cursor-pointer bg-white"
                  >
                    <option value="">평형을 선택해주세요</option>
                    <option value="59A">59A㎡</option>
                    <option value="59B">59B㎡</option>
                    <option value="59C">59C㎡</option>
                    <option value="84A">84A㎡</option>
                    <option value="84B">84B㎡</option>
                    <option value="84C">84C㎡</option>
                  </select>
                </div>

                {/* 방문 날짜 & 시간 */}
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label htmlFor="visitDate" className="block text-[12px] md:text-sm font-medium text-gray-900 mb-1.5 md:mb-2">
                      방문 날짜 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="visitDate"
                      name="visitDate"
                      value={formData.visitDate}
                      onChange={handleChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-2 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a2b5c] focus:border-transparent outline-none transition-all text-[11px] md:text-sm cursor-pointer bg-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="visitTime" className="block text-[12px] md:text-sm font-medium text-gray-900 mb-1.5 md:mb-2">
                      방문 시간 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="visitTime"
                      name="visitTime"
                      value={formData.visitTime}
                      onChange={handleChange}
                      required
                      className="w-full px-2 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a2b5c] focus:border-transparent outline-none transition-all text-[11px] md:text-sm cursor-pointer bg-white"
                    >
                      <option value="">시간 선택</option>
                      <option value="09:00">09:00</option>
                      <option value="09:30">09:30</option>
                      <option value="10:00">10:00</option>
                      <option value="10:30">10:30</option>
                      <option value="11:00">11:00</option>
                      <option value="11:30">11:30</option>
                      <option value="12:00">12:00</option>
                      <option value="13:00">13:00</option>
                      <option value="13:30">13:30</option>
                      <option value="14:00">14:00</option>
                      <option value="14:30">14:30</option>
                      <option value="15:00">15:00</option>
                      <option value="15:30">15:30</option>
                      <option value="16:00">16:00</option>
                      <option value="16:30">16:30</option>
                      <option value="17:00">17:00</option>
                      <option value="17:30">17:30</option>
                      <option value="18:00">18:00</option>
                      <option value="19:00">19:00</option>
                      <option value="20:00">20:00</option>
                    </select>
                  </div>
                </div>

                {/* 방문 인원 */}
                <div>
                  <label htmlFor="visitors" className="block text-[12px] md:text-sm font-medium text-gray-900 mb-1.5 md:mb-2">
                    방문 인원
                  </label>
                  <select
                    id="visitors"
                    name="visitors"
                    value={formData.visitors}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a2b5c] focus:border-transparent outline-none transition-all text-[12px] md:text-sm cursor-pointer bg-white"
                  >
                    <option value="1">1명</option>
                    <option value="2">2명</option>
                    <option value="3">3명</option>
                    <option value="4">4명</option>
                    <option value="5">5명 이상</option>
                  </select>
                </div>

                {/* 개인정보 동의 */}
                <div className="flex items-start gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="agreePrivacy"
                    name="agreePrivacy"
                    checked={formData.agreePrivacy}
                    onChange={handleChange}
                    className="mt-0.5 md:mt-1 cursor-pointer accent-[#1a2b5c]"
                  />
                  <label htmlFor="agreePrivacy" className="text-[11px] md:text-[13px] text-gray-600 cursor-pointer leading-relaxed">
                    개인정보 수집 및 이용에 동의합니다. <span className="text-red-500">*</span><br />
                    <span className="text-[10px] md:text-[12px] text-gray-400">수집항목: 이름, 연락처 / 이용목적: 분양 상담 / 보유기간: 상담 완료 후 1년</span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting' || !formData.agreePrivacy}
                  className={`w-full py-3 md:py-3.5 rounded-lg text-[13px] md:text-[15px] font-semibold transition-all whitespace-nowrap ${
                    formData.agreePrivacy
                      ? 'bg-[#1a2b5c] text-white hover:bg-[#0f1a3d] cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {formStatus === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> 전송 중...
                    </span>
                  ) : '예약 신청하기'}
                </button>

                {formStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 md:p-4 bg-red-50 rounded-lg border border-red-200">
                    <i className="ri-error-warning-line text-red-600 text-lg md:text-xl"></i>
                    <p className="text-[12px] md:text-[14px] text-red-700 font-medium">전송에 실패했습니다. 잠시 후 다시 시도해주세요.</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* 모바일 고정 플로팅 CTA 바 */}
      <div className="fixed bottom-0 left-0 right-0 z-[90] md:hidden">
        <div className="h-[2px] bg-gradient-to-r from-[#b8972a] via-[#f0d060] to-[#b8972a]"></div>
        <div className="bg-gradient-to-r from-[#0a1229] via-[#1a2b5c] to-[#0a1229] px-3 py-3 flex items-center gap-2.5 shadow-2xl">
          <Link
            to="/reservation"
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl whitespace-nowrap cursor-pointer relative overflow-hidden group"
            style={{ background: 'linear-gradient(135deg, #b8972a 0%, #f0d060 40%, #c9a84c 70%, #b8972a 100%)' }}
          >
            <div className="absolute inset-0 bg-white/0 group-active:bg-white/10 transition-all"></div>
            <i className="ri-calendar-check-line text-[#0a1229] text-[18px]"></i>
            <span className="text-[14px] font-bold text-[#0a1229] relative z-10">방문예약</span>
          </Link>
          <a
            href="tel:1555-5475"
            onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-white rounded-xl whitespace-nowrap cursor-pointer relative overflow-hidden group border-2 border-white/30"
          >
            <div className="absolute inset-0 bg-[#1a2b5c]/0 group-active:bg-[#1a2b5c]/10 transition-all"></div>
            <i className="ri-phone-fill text-[#1a2b5c] text-[18px]"></i>
            <span className="text-[14px] font-bold text-[#1a2b5c] relative z-10">전화상담</span>
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
}
